import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import apiRequest from "../../utils/apiClient";
import { getUserTranscriptVideosUrl, getSentencesStreamUrl } from "../constants";
import { userContext } from "../../stores/users/userContext";
import { messageContext } from "../../stores/messages/messageContext";
import { IN_FLIGHT_STATUSES } from "../pages/myLibrary/constants/taskStatuses";
import CreateVideo from "./createVideo";
import { loadArchive, parseIdsParam } from "./archiveLoader";
import { mergeArchive } from "./multiSheet";
import { renameSpeakerIdentity, unlinkSpeaker, speakerGroups, loadIdentityMap, saveIdentityMap } from "./speakerIdentity";
import { archiveExportTitle } from "./archiveExport";
import { ARCHIVE_NOTES } from "./archiveNotes";

/**
 * Archive — several uploads as ONE sheet. Route /archive/?ids=6,9 (no ids → the picker).
 *
 * This is the CONTROLLER: it loads each transcript's stream (one at a time), merges them with
 * the tested core, owns the cross-video speaker identity map + the hidden-source set, and renders
 * the REAL ET (createVideo.jsx) in archive mode underneath a thin archive bar. The ET itself only
 * consumes the `archive` prop — see docs/superpowers/specs/2026-09-06-archive-et-design.md.
 */

const font = "Outfit, system-ui, sans-serif";
const CHERRY = "#b3123a";
const BAR_H = 42;

const fmtDur = (sec) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const m = Math.floor(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m ${String(s % 60).padStart(2, "0")}s`;
};

const ArchiveView = () => {
  const userStore = useContext(userContext);
  const messageStore = useContext(messageContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ids = useMemo(() => parseIdsParam(searchParams.get("ids")), [searchParams]);
  const idsKey = ids.join(",");

  const [phase, setPhase] = useState(ids.length ? "loading" : "picker"); // picker | loading | ready | error
  const [progress, setProgress] = useState(null);      // { index, total, transcriptId, percent }
  const [errorMsg, setErrorMsg] = useState("");
  const [merged, setMerged] = useState(null);          // mergeArchive() result
  const [speakerMap, setSpeakerMap] = useState({});    // { "6:A": "Trump" }
  const [hiddenSources, setHiddenSources] = useState(() => new Set());
  const [panel, setPanel] = useState(null);            // 'speakers' | 'info' | null
  const loadedForRef = useRef("");

  // ── Load + merge whenever the ids in the URL change ────────────────────────────────────
  useEffect(() => {
    if (!ids.length) { setPhase("picker"); setMerged(null); return undefined; }
    if (loadedForRef.current === idsKey && merged) return undefined;
    let cancelled = false;
    setPhase("loading");
    setErrorMsg("");
    setMerged(null);
    setHiddenSources(new Set());
    setPanel(null);
    (async () => {
      try {
        const loaded = await loadArchive(ids, {
          fetchStream: (id) => apiRequest(`${getSentencesStreamUrl}${id}/`, { method: "GET" }),
          onProgress: (p) => { if (!cancelled) setProgress(p); },
        });
        if (cancelled) return;
        const cut = loaded.filter((t) => t.incomplete);
        if (cut.length) {
          messageStore.setMessage(`Couldn't fully load ${cut.map((t) => `"${t.title}"`).join(", ")} — showing what arrived. Reload to retry.`, "error", 12000);
        }
        const a = mergeArchive(loaded);
        // eslint-disable-next-line no-console
        // Count CLIPS (not streamed rows): a transcript's stream also carries silence rows, so
        // "2496 + 1483" streamed is 1532 + 989 real clips. Printing the two different units side
        // by side made a healthy merge look like it had dropped half the list.
        const clipsOf = (t) => (t.sentences || []).filter((r) => r && !r.space).length;
        console.log(`[archive] merged ${a.rows.filter((r) => !r.space).length} clips from ${loaded.map((t) => `${t.title}:${clipsOf(t)}`).join(" + ")} (${a.rows.length} rows incl. silences)`);
        setSpeakerMap(loadIdentityMap(a.key));
        // The ET's breadcrumb/export title reads the store; there is no single transcript id.
        userStore.setUserTranscript(null, archiveExportTitle(a.sources), null, 0);
        loadedForRef.current = idsKey;
        setMerged(a);
        setPhase("ready");
      } catch (e) {
        if (cancelled) return;
        setErrorMsg((e && e.message) || "Couldn't load the archive.");
        setPhase("error");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  // Persist speaker links per combination of videos.
  useEffect(() => { if (merged) saveIdentityMap(merged.key, speakerMap); }, [speakerMap, merged]);

  // The health banner is fixed at the very top and would sit ON the archive bar — push it down
  // while the archive is open (HealthBanner reads --cc-banner-top), restore on leave.
  useEffect(() => {
    if (phase !== "ready") return undefined;
    const root = document.documentElement;
    root.style.setProperty("--cc-banner-top", `${BAR_H}px`);
    return () => { root.style.removeProperty("--cc-banner-top"); };
  }, [phase]);

  const onRenameSpeaker = (currentLabel, name) => {
    if (!merged) return;
    setSpeakerMap((prev) => renameSpeakerIdentity(merged.rows, prev, currentLabel, name));
  };
  const onUnlink = (key) => setSpeakerMap((prev) => unlinkSpeaker(prev, key));
  const toggleSource = (transcriptId) => {
    setHiddenSources((prev) => {
      const next = new Set(prev);
      const k = String(transcriptId);
      if (next.has(k)) next.delete(k); else next.add(k);
      // Never hide everything — that just looks like a broken sheet.
      if (merged && next.size >= merged.sources.length) return prev;
      return next;
    });
  };

  const archiveProp = useMemo(
    () => (merged ? { ...merged, speakerMap, hiddenSources, onRenameSpeaker } : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [merged, speakerMap, hiddenSources],
  );

  if (phase === "picker") return <ArchivePicker onOpen={(picked) => navigate(`/archive/?ids=${picked.join(",")}`)} />;

  if (phase === "loading" || phase === "error") {
    const p = progress;
    const src = p && merged == null ? p : null;
    return (
      <div style={{ minHeight: "100vh", background: "#0d0d0d", color: "#eee", fontFamily: font, display: "flex", alignItems: "center", justifyContent: "center", padding: 28 }}>
        <div style={{ textAlign: "center", maxWidth: 520 }}>
          <div style={{ fontSize: 26, fontWeight: 800, marginBottom: 8 }}>🗂 Archive</div>
          {phase === "error" ? (
            <>
              <div style={{ color: "#f87171", marginBottom: 18 }}>{errorMsg}</div>
              <button onClick={() => navigate("/archive/")} style={btn()}>← Pick videos</button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 15, color: "#bbb" }}>
                {src ? `Loading ${src.index + 1} of ${src.total} — transcript ${src.transcriptId} · ${src.percent}%` : "Loading…"}
              </div>
              <div style={{ height: 6, background: "#222", borderRadius: 3, marginTop: 14, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${src ? Math.round(((src.index + src.percent / 100) / src.total) * 100) : 5}%`, background: CHERRY, transition: "width .2s" }} />
              </div>
              <div style={{ fontSize: 12, color: "#666", marginTop: 10 }}>Videos load one at a time so nothing gets cut short.</div>
            </>
          )}
        </div>
      </div>
    );
  }

  // READY — the archive bar + the real ET in archive mode. The ET positions its content
  // absolutely inside the nearest positioned ancestor, so the wrapper is relative with a real
  // height (a height-less wrapper collapsed the virtualized list to 0px in the July build).
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", minHeight: 0, background: "#0d0d0d" }}>
      <ArchiveBar
        merged={merged}
        hiddenSources={hiddenSources}
        onToggleSource={toggleSource}
        speakerMap={speakerMap}
        onRenameSpeaker={onRenameSpeaker}
        onUnlink={onUnlink}
        panel={panel}
        setPanel={setPanel}
        onChangeSheets={() => navigate("/archive/")}
      />
      <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
        <CreateVideo archive={archiveProp} />
      </div>
    </div>
  );
};

const btn = (extra = {}) => ({
  padding: "9px 14px", background: CHERRY, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer",
  fontWeight: 700, fontSize: 13, fontFamily: font, ...extra,
});

/* ── The bar: source chips · Speakers · What's in Archive · change sheets ───────────────── */
const ArchiveBar = ({ merged, hiddenSources, onToggleSource, speakerMap, onRenameSpeaker, onUnlink, panel, setPanel, onChangeSheets }) => {
  const toggle = (name) => setPanel((p) => (p === name ? null : name));
  const chipBtn = (active) => ({
    display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, cursor: "pointer", border: "1px solid rgba(255,255,255,0.14)",
    background: active ? "rgba(255,255,255,0.08)" : "transparent", color: "#eee", fontSize: 12, fontWeight: 600, fontFamily: font, whiteSpace: "nowrap",
  });
  return (
    <div style={{ position: "relative", zIndex: 1400, height: BAR_H, flex: `0 0 ${BAR_H}px`, display: "flex", alignItems: "center", gap: 8, padding: "0 12px", background: "#141018", borderBottom: "1px solid rgba(255,255,255,0.08)", overflowX: "auto", overflowY: "visible" }}>
      <span style={{ fontFamily: font, fontWeight: 800, fontSize: 13, color: "#fff", whiteSpace: "nowrap" }}>🗂 Archive</span>
      <span style={{ fontFamily: font, fontSize: 11, color: "#888", whiteSpace: "nowrap" }}>{merged.sources.length} videos · {merged.rows.filter((r) => !r.space).length} clips · {fmtDur(merged.totalDuration)}</span>
      <span style={{ width: 1, height: 20, background: "rgba(255,255,255,0.12)" }} />
      {merged.sources.map((s) => {
        const hidden = hiddenSources.has(String(s.transcriptId));
        return (
          <button key={s.transcriptId} type="button" onClick={() => onToggleSource(s.transcriptId)} title={`${hidden ? "Show" : "Hide"} "${s.label}" (${s.clips} clips, ${fmtDur(s.duration)})`}
            style={{ ...chipBtn(!hidden), opacity: hidden ? 0.45 : 1, textDecoration: hidden ? "line-through" : "none", borderColor: hidden ? "rgba(255,255,255,0.14)" : s.color }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: s.color, flex: "0 0 auto" }} />
            <b style={{ color: s.color }}>{s.short}</b>
            <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}>{s.label}</span>
            <span style={{ color: "#888", fontWeight: 500 }}>{s.clips}</span>
          </button>
        );
      })}
      <span style={{ flex: 1 }} />
      <button type="button" onClick={() => toggle("speakers")} style={chipBtn(panel === "speakers")}>👥 Speakers</button>
      <button type="button" onClick={() => toggle("info")} style={chipBtn(panel === "info")}>ⓘ What's in Archive</button>
      <button type="button" onClick={onChangeSheets} style={chipBtn(false)}>← Change videos</button>
      {panel === "speakers" && <SpeakersPanel merged={merged} speakerMap={speakerMap} onRenameSpeaker={onRenameSpeaker} onUnlink={onUnlink} onClose={() => setPanel(null)} />}
      {panel === "info" && <InfoPanel onClose={() => setPanel(null)} />}
    </div>
  );
};

const panelStyle = { position: "fixed", top: BAR_H + 6, right: 12, width: 440, maxWidth: "calc(100vw - 24px)", maxHeight: "70vh", overflowY: "auto", background: "#17131c", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 12, boxShadow: "0 18px 50px rgba(0,0,0,0.6)", padding: 14, zIndex: 2100 /* above the fixed health banner */, fontFamily: font, color: "#eee" };

/* Link the same person across videos: same name = same speaker. */
const SpeakersPanel = ({ merged, speakerMap, onRenameSpeaker, onUnlink, onClose }) => {
  const groups = useMemo(() => speakerGroups(merged.rows, speakerMap), [merged, speakerMap]);
  const [drafts, setDrafts] = useState({});
  const commit = (label) => {
    const v = drafts[label];
    if (v === undefined || v === label) return;
    onRenameSpeaker(label, v);
    setDrafts((d) => { const n = { ...d }; delete n[label]; return n; });
  };
  return (
    <div style={panelStyle}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
        <b style={{ fontSize: 14 }}>Who's who across the videos</b>
        <span style={{ flex: 1 }} />
        <button type="button" onClick={onClose} style={{ background: "none", border: "none", color: "#aaa", cursor: "pointer", fontSize: 16 }}>✕</button>
      </div>
      <div style={{ fontSize: 12, color: "#999", lineHeight: 1.5, marginBottom: 12 }}>
        Each video labels its own speakers (A·1 = speaker A of video 1). <b style={{ color: "#ddd" }}>Type the same name on two of them to say they're the same person</b> — the sheet, the filters and Cherry then treat them as one. Blank a name to unlink.
      </div>
      {groups.map((g) => (
        <div key={g.label} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderTop: "1px solid rgba(255,255,255,0.07)" }}>
          <input
            value={drafts[g.label] ?? g.label}
            onChange={(e) => setDrafts((d) => ({ ...d, [g.label]: e.target.value }))}
            onBlur={() => commit(g.label)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
            style={{ width: 150, padding: "6px 8px", borderRadius: 6, border: `1px solid ${g.linked ? "#10b981" : "rgba(255,255,255,0.18)"}`, background: "#0e0b12", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 13 }}
          />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, flex: 1 }}>
            {g.members.map((m) => (
              <span key={m.key} title={`${m.sourceLabel} · speaker ${m.rawSpeaker} · ${m.clips} clips`} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 8px", borderRadius: 999, border: `1px solid ${m.color}`, fontSize: 11.5, color: "#ddd" }}>
                <b style={{ color: m.color }}>{m.short}</b> {m.rawSpeaker} <span style={{ color: "#888" }}>{m.clips}</span>
                {speakerMap[m.key] && (
                  <button type="button" title="Unlink — back to its own label" onClick={() => onUnlink(m.key)} style={{ background: "none", border: "none", color: "#aaa", cursor: "pointer", padding: 0, fontSize: 12, lineHeight: 1 }}>✕</button>
                )}
              </span>
            ))}
            {g.linked && <span style={{ fontSize: 11, color: "#10b981", alignSelf: "center" }}>linked across {new Set(g.members.map((m) => m.short)).size} videos</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

const InfoPanel = ({ onClose }) => (
  <div style={panelStyle}>
    <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
      <b style={{ fontSize: 14 }}>What's in Archive</b>
      <span style={{ flex: 1 }} />
      <button type="button" onClick={onClose} style={{ background: "none", border: "none", color: "#aaa", cursor: "pointer", fontSize: 16 }}>✕</button>
    </div>
    {ARCHIVE_NOTES.map((sec) => (
      <div key={sec.title} style={{ marginTop: 10 }}>
        <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.6, color: "#888", marginBottom: 4 }}>{sec.title}</div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.55, color: "#ddd" }}>
          {sec.items.map((it) => <li key={it}>{it}</li>)}
        </ul>
      </div>
    ))}
  </div>
);

/* ── Picker (no ids in the URL): tick 2+ finished uploads ──────────────────────────────── */
const ArchivePicker = ({ onOpen }) => {
  const [videos, setVideos] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [picked, setPicked] = useState(() => new Set());
  const [q, setQ] = useState("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest(getUserTranscriptVideosUrl, { method: "GET" });
        const body = await res.json().catch(() => []);
        if (cancelled) return;
        setVideos((Array.isArray(body) ? body : []).filter((v) => v && v.id && !(v.task_status && IN_FLIGHT_STATUSES.has(v.task_status))));
      } catch (e) { if (!cancelled) setVideos([]); }
      finally { if (!cancelled) setListLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);
  const toggle = (id) => setPicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const shown = videos.filter((v) => !q || String(v.title || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div style={{ minHeight: "100vh", background: "#0d0d0d", color: "#eee", fontFamily: font, padding: 28, maxWidth: 760, margin: "0 auto" }}>
      <div style={{ fontSize: 26, fontWeight: 800 }}>🗂 Archive</div>
      <div style={{ fontSize: 14, color: "#999", marginBottom: 18 }}>Open several uploads as ONE sheet — filter, sort, search, tick, ask Cherry and export across all of them.</div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a video…" style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 8, border: "1px solid #2a2a2a", background: "#151515", color: "#fff", fontFamily: font, fontSize: 14, marginBottom: 10 }} />
      {listLoading ? <div style={{ color: "#888" }}>Loading your videos…</div> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 18, maxHeight: "55vh", overflowY: "auto" }}>
          {shown.map((v) => (
            <label key={v.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, cursor: "pointer", padding: "9px 10px", borderRadius: 8, background: picked.has(v.id) ? "#1d1418" : "transparent", border: `1px solid ${picked.has(v.id) ? CHERRY : "transparent"}` }}>
              <input type="checkbox" checked={picked.has(v.id)} onChange={() => toggle(v.id)} style={{ accentColor: CHERRY }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{v.title || `Transcript ${v.id}`}</span>
              <span style={{ color: "#666", fontSize: 12, whiteSpace: "nowrap" }}>{v.clips_count != null ? `${v.clips_count} clips · ` : ""}{v.duration_in_seconds ? fmtDur(v.duration_in_seconds) : ""}</span>
            </label>
          ))}
          {!shown.length && <div style={{ color: "#777", padding: 10 }}>No finished uploads match.</div>}
        </div>
      )}
      <button type="button" disabled={picked.size < 1} onClick={() => onOpen([...picked])} style={btn({ padding: "12px 20px", fontSize: 15, background: picked.size ? CHERRY : "#333", cursor: picked.size ? "pointer" : "default" })}>
        Open {picked.size || ""} as one sheet
      </button>
      <div style={{ fontSize: 12, color: "#666", marginTop: 10 }}>Pick two or more. Tip: in My Library → Uploads, the Combine button does the same thing.</div>
    </div>
  );
};

export default observer(ArchiveView);
